import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import MarketingLayout from './components/MarketingLayout'

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
}
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }

const effectiveDate = 'August 18, 2026'

const sections = [
  {
    id: 'information-we-collect',
    title: '1. Information We Collect',
    content: `We collect information you provide directly to us when creating an account, submitting a service request, or contacting us. This includes:

**Account Information:** Name, email address, phone number, password (stored as a secure bcrypt hash), address, city, state, ZIP code, and county.

**Service Request Data:** Service type (Plumbing, Electrical, or HVAC), description of your issue, photos you submit, and your service location.

**Payment Information:** We collect and process payment information through Stripe. SOS SMS does not store full credit card numbers. Payment authorization and capture are handled securely by Stripe's infrastructure.

**Communications:** SMS messages sent to and received from our Twilio-powered dispatch system are logged to ensure service quality and resolve disputes.

**Usage Data:** Log data including IP addresses, browser type, pages visited, and actions taken within the platform.

**Provider-Specific Data:** For service providers, we additionally collect: business name, company information, years of experience, service types offered, coverage areas (ZIP codes and counties), license documents, insurance documents, Stripe Connect account details, and technician information.`,
  },
  {
    id: 'how-we-use',
    title: '2. How We Use Your Information',
    content: `SOS SMS uses the information collected to:

• **Provide the Service:** Match customers with nearby qualified service providers, coordinate dispatch logistics, and process payments.

• **SMS Communication:** Send and receive SMS messages via Twilio to facilitate the dispatch process, confirm appointments, and collect ratings.

• **Payment Processing:** Authorize and capture trip charges from customers and distribute payouts to providers through Stripe Connect.

• **Account Management:** Maintain and update your profile, verify your identity, and manage authentication sessions via JSON Web Tokens (JWT).

• **Provider Verification:** Review license and insurance documents to verify provider credentials before platform approval.

• **Platform Improvement:** Analyze usage patterns, job completion rates, and dispatch metrics to improve our algorithms and user experience.

• **Customer Support:** Respond to inquiries, resolve disputes, and handle complaints.

• **Legal Compliance:** Comply with applicable laws, regulations, and law enforcement requests.`,
  },
  {
    id: 'data-storage',
    title: '3. Data Storage & Security',
    content: `**Database:** All data is stored in MongoDB, a NoSQL database hosted on secure cloud infrastructure (Amazon Web Services).

**Password Security:** All passwords are hashed using bcryptjs before storage. We never store plaintext passwords.

**Authentication:** Sessions are managed using JSON Web Tokens (JWT) with a 7-day expiration. Tokens are stored in the user's browser localStorage.

**Payment Security:** Payment data is handled entirely by Stripe, which is PCI DSS Level 1 compliant - the highest level of payment security certification.

**Document Storage:** License and insurance documents uploaded by providers are stored securely on our servers with restricted access.

**Data Transmission:** All data transmitted between your browser and our servers is encrypted using HTTPS/TLS protocols.

**Access Controls:** Internal access to user data is restricted to authorized personnel only, using role-based access controls (Admin, Provider, Customer roles).`,
  },
  {
    id: 'data-sharing',
    title: '4. Information Sharing & Disclosure',
    content: `We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:

**Service Dispatch:** When a provider is assigned to your job, your first name, service address, and service description are shared with that provider to facilitate the service visit.

**Payment Processors:** We share necessary payment information with Stripe to process transactions.

**SMS Providers:** We use Twilio to send and receive SMS messages. Your phone number is used by Twilio solely for message delivery.

**Google Services:** Provider business information may be synchronized with Google Business Profile and Google Places API for business verification and review integration.

**Law Enforcement:** We may disclose your information if required to do so by law or in response to valid requests from public authorities (courts, law enforcement).

**Business Transfers:** In the event of a merger, acquisition, or sale of assets, user data may be transferred to the acquiring entity, subject to the same privacy commitments.

**SMS Consent:** Mobile information and SMS opt-in consent will not be shared with or sold to any third party or affiliate for marketing purposes.`,
  },
  {
    id: 'cookies',
    title: '5. Cookies & Local Storage',
    content: `SOS SMS uses browser localStorage (not traditional cookies) to store authentication tokens and user preferences. This allows you to remain logged in between sessions.

**What We Store Locally:**
• Authentication JWT token (expires after 7 days)
• UI theme preferences

**Advertising & Conversion Tracking:**
We use the Meta (Facebook) Pixel for conversion measurement only. This helps us understand whether visitors who see our advertisements complete actions such as joining the waitlist or creating an account.

We do not build retargeting audiences, custom audiences, or lookalike audiences from visitor data, and we do not share browsing information with advertising networks for behavioral advertising purposes.

You can clear this data at any time by clearing your browser's local storage or logging out of the platform, which removes the authentication token.`,
  },
  {
    id: 'sms-communications',
    title: '6. SMS Communications',
    content: `SOS SMS is primarily an SMS-driven platform. By checking the SMS consent box during signup, you expressly agree to receive SMS messages from SOS SMS related to:

• Service request status updates
• Provider assignment notifications
• ETA updates
• Payment confirmations
• Service rating requests

Message and data rates may apply depending on your mobile carrier. Message frequency varies. Reply STOP at any time to opt out of SMS messages, or reply HELP for assistance. Note that opting out of transactional SMS messages may prevent us from delivering dispatch-related services.

All SMS conversations are logged in our system for quality assurance, dispute resolution, and service improvement purposes.`,
  },
  {
    id: 'data-retention',
    title: '7. Data Retention',
    content: `We retain your personal data for as long as your account is active or as needed to provide services. Specifically:

• **Account Data:** Retained for the life of the account plus 3 years after account deletion.
• **Job Records:** Service job records are retained for 7 years for legal and financial compliance purposes.
• **Payment Records:** Stripe payment records are retained in accordance with Stripe's data retention policies and financial record-keeping requirements.
• **SMS Logs:** SMS conversation logs are retained for 2 years for quality assurance and dispute resolution.
• **Provider Documents:** License and insurance documents are retained during the provider's active account period plus 1 year.

You may request deletion of your account and associated data by contacting us at support@sossms.com. Note that certain data may be retained longer where required by law or for legitimate business purposes.`,
  },
  {
    id: 'your-rights',
    title: '8. Your Rights',
    content: `Depending on your location, you may have the following rights regarding your personal data:

**Access:** Request a copy of the personal data we hold about you.

**Correction:** Request correction of inaccurate or incomplete information.

**Deletion:** Request deletion of your personal data, subject to certain legal requirements.

**Portability:** Request your data in a portable format.

**Objection:** Object to certain processing of your data.

**Opt-Out:** Opt out of marketing communications at any time.

To exercise any of these rights, please contact us at support@sossms.com. We will respond to your request within 30 days.`,
  },
  {
    id: 'children',
    title: '9. Children\'s Privacy',
    content: `SOS SMS is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you believe we have inadvertently collected information from a minor, please contact us immediately at support@sossms.com and we will take steps to delete the information.`,
  },
  {
    id: 'changes',
    title: '10. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. When we do, we will update the "Effective Date" at the top of this page and notify registered users via email or in-platform notification. Your continued use of SOS SMS after the effective date of changes constitutes acceptance of the updated policy. We encourage you to review this page periodically.`,
  },
  {
    id: 'contact',
    title: '11. Contact Us',
    content: `If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:

**SOS SMS**
Email: support@sossms.com
Subject Line: Privacy Policy Inquiry

We are committed to working with you to resolve any concerns about your privacy.`,
  },
]

function Section({ section }) {
  return (
    <motion.div id={section.id} variants={fadeUp} className="scroll-mt-24">
      <h2 className="text-xl font-extrabold text-gray-900 mb-4">{section.title}</h2>
      <div className="text-gray-600 leading-relaxed text-sm space-y-3">
        {section.content.split('\n\n').map((para, i) => {
          if (para.startsWith('•')) {
            return (
              <ul key={i} className="space-y-1.5 ml-4">
                {para.split('\n').map((line, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <span className="text-primary mt-1 flex-shrink-0">•</span>
                    <span dangerouslySetInnerHTML={{ __html: line.replace(/^•\s*/, '').replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-800">$1</strong>') }} />
                  </li>
                ))}
              </ul>
            )
          }
          return (
            <p
              key={i}
              dangerouslySetInnerHTML={{
                __html: para.replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-800 font-semibold">$1</strong>'),
              }}
            />
          )
        })}
      </div>
    </motion.div>
  )
}

function PrivacyPolicyPage() {
  useEffect(() => {
    document.title = 'Privacy Policy | SOS SMS'
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      'Learn how SOS SMS collects, stores, and protects your personal data, including account, payment, and SMS communication information.'
    )
  }, [])

  return (
    <MarketingLayout>

      {/* Hero */}
      <section className="bg-gradient-to-br from-green-900 via-green-800 to-emerald-700 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs><pattern id="grid5" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" /></pattern></defs>
            <rect width="100%" height="100%" fill="url(#grid5)" />
          </svg>
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-green-300 font-semibold text-sm uppercase tracking-wider mb-3">Legal</motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl font-extrabold mb-4">Privacy Policy</motion.h1>
            <motion.p variants={fadeUp} className="text-green-100">Effective Date: {effectiveDate}</motion.p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" className="w-full h-12 fill-white" preserveAspectRatio="none">
            <path d="M0,32 C360,60 1080,0 1440,32 L1440,60 L0,60 Z" />
          </svg>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-12">

            {/* Table of Contents */}
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Contents</h3>
                <nav className="space-y-1">
                  {sections.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="block text-sm text-gray-500 hover:text-primary py-1 border-l-2 border-transparent hover:border-primary pl-3 transition-colors"
                    >
                      {s.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <main className="lg:col-span-3">
              <div className="bg-green-50 border border-green-100 rounded-2xl p-5 mb-10 text-sm text-gray-700">
                <strong className="text-gray-900">Your privacy matters to us.</strong> This Privacy Policy explains how SOS SMS collects, uses, stores, and protects your personal information. By using our platform, you agree to the practices described in this document.
              </div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={stagger}
                className="space-y-12 divide-y divide-gray-100"
              >
                {sections.map((section) => (
                  <div key={section.id} className="pt-10 first:pt-0">
                    <Section section={section} />
                  </div>
                ))}
              </motion.div>

              <div className="mt-12 pt-8 border-t border-gray-100 text-center">
                <p className="text-gray-500 text-sm mb-4">Have questions about our privacy practices?</p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-green-700 transition-colors text-sm"
                >
                  Contact Us
                </Link>
              </div>
            </main>

          </div>
        </div>
      </section>

    </MarketingLayout>
  )
}

export default PrivacyPolicyPage
